/* eslint-disable react/function-component-definition */
import React, {
  useImperativeHandle, useState, useRef, useMemo,
} from 'react';
import { getSubscribe } from '$/common/utils/subscribeUtil';
import type { SubscriptionContract } from '@meitu/subscribe';
import { useLanguageStore } from '$/store/languageStore';
import ConfirmModal, { ConfirmModalRef } from '$/components/ConfirmModal';
import { message } from '$/components/Message';
import { openVipModal } from '$/store/useVipModalStore';
import IconFont from '$/components/IconFont';
import styles from './index.module.scss';

export interface RenewalManagementModalRef {
  open: () => void;
}

interface RenewalManagementModalProps {
  contractList: SubscriptionContract[];
}
// 订阅管理弹窗，保留原续期管理流程并补齐无订阅空态。
const RenewalManagementModal: React.ForwardRefRenderFunction<RenewalManagementModalRef, RenewalManagementModalProps> = (props, ref) => {
  const { contractList } = props;
  const [currentStep, setCurrentStep] = useState(0);
  const [commodityId, setCommodityId] = useState<string>();
  const confirmModalRef = useRef<ConfirmModalRef>(null);
  const languageStore = useLanguageStore((state) => state.languageStore);

  // 取消当前自动续期合约，并推进确认流程。
  const terminateContract = async () => {
    try {
      const instance = await getSubscribe();
      await instance?.terminateContract(
        { contractId: `${currentRenewal?.contractId}` as string },
      );
      setCurrentStep(currentStep + 1);
    } catch (err: any) {
      message.error(err.message);
    }
  };

  // 无订阅合约时引导用户进入会员开通流程。
  const openSubscribeModal = () => {
    openVipModal?.();
    setCurrentStep(0);
    confirmModalRef.current?.closeModal();
  };

  useImperativeHandle(ref, () => ({
    open: () => {
      confirmModalRef.current?.openModal();
    },
  }));
  const confirmTextArr = [
    {
      text: languageStore.smartcatTranslationText((v) => v.roboneo_web_ot),
      onclick: () => {
        confirmModalRef.current?.closeModal();
      },
    },
    {
      text: languageStore.smartcatTranslationText((v) => v.Cancel),
      onclick: () => {
        setCurrentStep(currentStep + 1);
      },
    },
    {
      text: languageStore.smartcatTranslationText((v) => v.continue),
      onclick: terminateContract,
    },
    {
      text: languageStore.smartcatTranslationText((v) => v.Go_to_renew),
      onclick: () => {
        openVipModal?.();
        setCurrentStep(0);
        confirmModalRef.current?.closeModal();
      },
    },
  ];
  const currentRenewal = useMemo(() => {
    return contractList.find((item) => item.commodityId === commodityId);
  }, [commodityId, contractList]);

  const hasContract = !!contractList?.length;

  return (
    <ConfirmModal
      ref={confirmModalRef}
      title={languageStore.smartcatTranslationText((v) => v.subscription_management)}
      confirmText={hasContract ? confirmTextArr[currentStep].text : languageStore.smartcatTranslationText((v) => v.Sign_up_vip)}
      onConfirm={() => {
        if (!hasContract) {
          openSubscribeModal();
          return;
        }
        confirmTextArr[currentStep].onclick?.();
      }}
      onClose={() => {
        setCurrentStep(0);
      }}
      buttonStyle={{ margin: '0 18px 18px', width: 'auto' }}
    >
      <div className={styles['renewal-management-container']}>
        { currentStep === 0 && !hasContract && (
          <div className={styles['empty-subscription']}>
            {languageStore.smartcatTranslationText((v) => v.Roboneo_web_no_data)}
          </div>
        )}
        { currentStep === 0 && hasContract && (contractList || []).map((item, index) => (
          <div
            className={styles['renewal-item']}
              // eslint-disable-next-line react/no-array-index-key
            key={index}
            onClick={() => {
              setCurrentStep(currentStep + 1);
              setCommodityId(item.commodityId);
            }}
          >
            <div className={styles['renewal-item-info']}>
              <div className={styles['renewal-item-title']}>
                {item.productName}
              </div>
              <div className={styles['renewal-item-deadline']}>
                {item?.contractExplain?.nextWithholdTimeExplain}
              </div>
            </div>
            <div className="renewal-item-icon">
              <IconFont name="chevronRightBlack" fontSize={18} color="var(--content-quaternary)" />
            </div>
          </div>
        ))}
        { currentStep === 1 && (
        <div
          className={styles['renewal-item']}
        >
          <div className={styles['renewal-item-info']}>
            <div className={styles['renewal-item-title']}>
              {currentRenewal?.productName}
            </div>
            <div className={styles['renewal-item-deadline']}>
              {currentRenewal?.contractExplain?.nextWithholdTimeExplain}
            </div>
            <div className={styles['renewal-item-deadline']}>
              {currentRenewal?.contractExplain?.nextWithholdAmountExplain }
            </div>
          </div>
        </div>
        )}
        {currentStep === 2 && (
        <div className={styles['cancel-text']}>
          { currentRenewal?.contractExplain?.cancelingRenewalExplain }
          { currentRenewal?.contractExplain?.resumeSubExplain}
        </div>
        )}
        {currentStep === 3 && (
        <div className={styles['cancel-text']}>
          { currentRenewal?.contractExplain?.canceledRenewalExplain }
          { currentRenewal?.contractExplain?.resumeSubExplain}
        </div>
        )}
      </div>
    </ConfirmModal>
  );
};
export default React.forwardRef(RenewalManagementModal);
